// ---------------------------------------------------------------------------
// Sandbox Provisioning Request form model + email builder.
//
// Fields mirror the "Sandbox Provisioning" sheet from the Data Engineering
// Request Forms workbook. On submit, opens an Outlook compose window with
// the form summary emailed to enterprisedata@loandepot.com.
// ---------------------------------------------------------------------------

import {
  formatDate,
  OUTLOOK_COMPOSE_BASE,
  REQUEST_RECIPIENT,
} from './intake'

export interface SandboxProvisioningValues {
  sandboxName: string
  requestor: string
  businessTeam: string
  purpose: string
  duration: string
  usersRequiringAccess: string
  dataAssetsRequired: string
  expectedDataVolume: string
  securityClassification: string
  requiredByDate: string
  approvalObtained: string
}

export const SANDBOX_INITIAL: SandboxProvisioningValues = {
  sandboxName: '',
  requestor: '',
  businessTeam: '',
  purpose: '',
  duration: '30 Days',
  usersRequiringAccess: '',
  dataAssetsRequired: '',
  expectedDataVolume: '',
  securityClassification: 'Public',
  requiredByDate: formatDate(new Date()),
  approvalObtained: '',
}

const SUMMARY_SECTIONS: {
  title: string
  fields: [keyof SandboxProvisioningValues, string][]
}[] = [
  {
    title: 'SANDBOX PROVISIONING REQUEST',
    fields: [
      ['sandboxName', 'Sandbox Name'],
      ['requestor', 'Requestor'],
      ['businessTeam', 'Business Team'],
      ['purpose', 'Purpose of Sandbox'],
      ['duration', 'Duration'],
      ['usersRequiringAccess', 'Users Requiring Access'],
      ['dataAssetsRequired', 'Data Assets Required'],
      ['expectedDataVolume', 'Expected Data Volume'],
      ['securityClassification', 'Security Classification'],
      ['requiredByDate', 'Required By Date'],
      ['approvalObtained', 'Approval Obtained'],
    ],
  },
]

export const SANDBOX_SUBJECT = 'Prism Portal Request - Sandbox Provisioning'

export function buildSandboxEmailBody(
  values: SandboxProvisioningValues
): string {
  const blocks: string[] = []
  for (const section of SUMMARY_SECTIONS) {
    const filled = section.fields.filter(
      ([key]) => String(values[key] ?? '').trim().length > 0
    )
    if (filled.length === 0) continue
    const lines = [section.title]
    for (const [key, label] of filled) {
      lines.push(`  ${label}: ${String(values[key]).trim()}`)
    }
    blocks.push(lines.join('\r\n'))
  }
  return blocks.join('\r\n\r\n')
}

export function buildSandboxOutlookUrl(
  values: SandboxProvisioningValues,
  loginHint?: string | null
): string {
  const params = new URLSearchParams({
    to: REQUEST_RECIPIENT,
    subject: SANDBOX_SUBJECT,
    body: buildSandboxEmailBody(values),
  })
  if (loginHint && loginHint.trim()) {
    params.set('login_hint', loginHint.trim())
  }
  const query = params.toString().replace(/\+/g, '%20')
  return `${OUTLOOK_COMPOSE_BASE}?${query}`
}
